/* eslint-disable no-console,@typescript-eslint/ban-ts-comment */

/**
 * CalmLogger brought over from https://github.com/calm/web/tree/master/libraries/logger
 */

import { datadogLogs } from '@datadog/browser-logs';
import pick from 'lodash/pick';
import createLogger, { Logger } from 'pino';
import { sanitizingStringifyReplacer } from './sanitizer';
import { JSONValue } from './types';

export { initDatadogLogs } from './datadog';

export interface logData {
    [key: string]: JSONValue | undefined;
}

export const getSanitizedLogData = (data?: logData): logData => {
    if (!data) {
        return {};
    }
    return JSON.parse(JSON.stringify(data, sanitizingStringifyReplacer()));
};

function hasWindowAndDocument() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}

const networkShape = {
    status: true,
    statusText: true,
    headers: true,
    config: ['method', 'url', 'params', 'headers', 'data'],
    request: ['res.headers'],
    data: true,
} as const;
const networkKeys = Object.keys(networkShape);

// TODO: how might we sanitize errors without destroying the value we're passed in?
// Currently this function destructively modifies the value it is passed
export const getSanitizedError = (error: Error): Error => {
    if (!error) {
        return error;
    }
    const errorKeys = Object.keys(error);
    const isNetworkError =
        errorKeys.includes('status') && errorKeys.includes('statusText');
    errorKeys.forEach((key) => {
        if (['type', 'message', 'stack', 'code'].includes(key)) {
            return;
        }
        try {
            // @ts-ignore
            let value = error[key];
            // Network errors have a lot of nested objects, so we only want to keep the most important ones
            if (isNetworkError) {
                if (networkKeys.includes(key)) {
                    const keysToPick =
                        networkShape[key as keyof typeof networkShape];
                    if (Array.isArray(keysToPick)) {
                        value = pick(value, keysToPick);
                    }
                    if (key === 'config' && value.data) {
                        try {
                            value.data = JSON.parse(value.data);
                        } catch {
                            // ignore parsing errors for payload data
                        }
                    }
                } else {
                    // @ts-ignore
                    delete error[key];
                    value = undefined;
                }
            }
            if (value) {
                // @ts-ignore
                error[key] = JSON.parse(
                    JSON.stringify(value, sanitizingStringifyReplacer())
                );
            }
        } catch {
            // ignore any errors when mucking about with formatting
        }
    });
    console.log('error', error);
    return error;
};

const formatForBrowser =
    (logFn: typeof console.log): ((o: object) => void) =>
    (o: object): void => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { level, time, err, msg, ...data } = o as {
            level: string | number;
            time: number;
            err?: Error;
            msg: string;
        };
        logFn(...[msg, data, err].filter(Boolean));
    };

export class CalmLogger {
    private logger: Logger;
    private shouldSkipConsoleLog: boolean;

    public constructor(
        childOptions: Parameters<
            ReturnType<typeof createLogger>['child']
        >[0] = {},
        shouldSkipConsoleLog?: boolean
    ) {
        const pino = createLogger({
            // pino represents log levels as numbers, while
            // Datadog expects them as strings. This formatter
            // ensures that the log level is sent as a string.
            formatters: {
                level(level) {
                    return { level };
                },
            },
            browser: {
                write: {
                    fatal: formatForBrowser(console.error),
                    error: formatForBrowser(console.error),
                    warn: formatForBrowser(console.warn),
                    info: formatForBrowser(console.info),
                    debug: formatForBrowser(console.debug),
                    trace: formatForBrowser(console.debug),
                },
            },
            errorKey: 'error',
        });
        this.logger = pino.child(childOptions);
        this.shouldSkipConsoleLog = shouldSkipConsoleLog ?? false;
    }

    public info(msg: string, data?: logData): void {
        const cleanData = getSanitizedLogData(data);
        if (!this.shouldSkipConsoleLog || !hasWindowAndDocument()) {
            this.logger.info(cleanData, msg);
        }
        if (hasWindowAndDocument()) {
            datadogLogs.logger.info(msg, cleanData);
        }
    }

    public warn(msg: string, data?: logData): void {
        const cleanData = getSanitizedLogData(data);
        if (!this.shouldSkipConsoleLog || !hasWindowAndDocument()) {
            this.logger.warn(cleanData, msg);
        }
        if (hasWindowAndDocument()) {
            datadogLogs.logger.warn(msg, cleanData);
        }
    }

    public debug(msg: string, data?: logData): void {
        const cleanData = getSanitizedLogData(data);
        if (!this.shouldSkipConsoleLog || !hasWindowAndDocument()) {
            this.logger.debug(cleanData, msg);
        }
        if (hasWindowAndDocument()) {
            datadogLogs.logger.debug(msg, cleanData);
        }
    }

    public fatal(msg: string, data?: logData): void {
        const cleanData = getSanitizedLogData(data);
        this.logger.fatal(cleanData, msg);
    }

    public error(msg: string, data: logData, err: Error): void {
        const cleanData = getSanitizedLogData(data);
        const cleanError = getSanitizedError(err);
        if (!this.shouldSkipConsoleLog || !hasWindowAndDocument()) {
            this.logger.error({ ...cleanData, error: cleanError }, msg);
        }
        if (hasWindowAndDocument()) {
            datadogLogs.logger.error(msg, cleanData, cleanError);
        }
    }
}

export const logger = new CalmLogger(
    process.env.NODE_ENV === 'production'
        ? {
              service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
          }
        : {}
);
